import { useMutation } from "@tanstack/react-query";
import { ListCommanWay } from "API/commonApi";
import { Img } from "components/Img";
import moment from "moment";
import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";

// VIA routing table: shipper -> destination -> VIA code
const VIA_ROUTES: Record<string, Record<string, string>> = {
  AHMEDABAD: {
    PADRA: "BDQ",
    HOSUR: "BLR",
    MYSORE: "BLR",
    NAYUDUPET: "BLR",
    ALLAHABAD: "DEL",
    BHATINDA: "DEL",
    CHANDIGARH: "DEL",
    DEHRADUN: "DEL",
    GURGAON: "DEL",
    HARIDWAR: "DEL",
    KANPUR: "DEL",
    LUDHIANA: "DEL",
    MANESAR: "DEL",
    PATHANKOT: "DEL",
    ROHTAK: "DEL",
    RUDRAPUR: "DEL",
    VARANASI: "DEL",
    VIJAYAWADA: "HYD",
    VISHAKAPATNAM: "HYD",
    BHUBANESHWAR: "CCU",
    SILIGURI: "CCU",
    SHOLINGANALLUR: "MAA",
    AMRAVATI: "BOM",
    AURANGABAD: "BOM",
    BEED: "BOM",
    BELGAUM: "BOM",
    KOLHAPUR: "BOM",
    NASIK: "BOM",
    "NASIK ROAD": "BOM",
    CHAKAN: "PNQ",
  },
};

export type MultiAwbLabelProps = {
  awbDataList: any[];
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Maps booking list API response item to AwbLabel format
 */
export const mapBookingToAwbData = (booking: any) => ({
  awb_no: booking.awb_no,
  bookingDate: booking.date,
  destination: booking.destination,
  shipper_city: booking.shipper_city,
  consignee: booking.consignee,
  address: booking.dest_addr || "", // New field from backend
  pincode: booking.dest_pincode,
  pickup_code: booking.pickup_code,
  is10_30: booking.is10_30,
  holiday: booking.holiday,
  dox_or_non_dox: booking.dox_or_non_dox,
  innerItemsNos: booking.innerItemsNos || 1, // Default to 1 if null/0
  isFod: booking.isFod,
  isCod: booking.isCod,
  _assign_to_booking: {
    shipper_city: booking.shipper_city,
    pickup_code: booking.pickup_code,
    date: booking.date,
  },
});

export const MultiAwbLabel: React.FC<MultiAwbLabelProps> = ({ awbDataList = [], isOpen, onClose }) => {
  const [cityCodes, setCityCodes] = useState<Record<string, string>>({});
  const [loadingCities, setLoadingCities] = useState(false);

  const { mutate: cityCodesMutate }: any = useMutation(
    (names: string[]) => ListCommanWay("/admin/city/list", { city_name: names }, {}),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          const obj: Record<string, string> = { ...cityCodes };
          try {
            data.data.data.forEach((city: any) => {
              obj[city.city_name] = city.cityCode;
            });
            setCityCodes(obj);
          } catch (ignored) {}
        }
        setLoadingCities(false);
      },
      onError: () => {
        setLoadingCities(false);
      },
    }
  );

  useEffect(() => {
    if (awbDataList.length > 0) {
      // Collect all unique cities from the list
      const cities = new Set<string>();
      awbDataList.forEach((awb) => {
        if (awb.destination) cities.add(awb.destination);
        if (awb.shipper_city) cities.add(awb.shipper_city);
        if (awb._assign_to_booking?.shipper_city) cities.add(awb._assign_to_booking.shipper_city);
      });
      if (cities.size > 0) {
        setLoadingCities(true);
        cityCodesMutate(Array.from(cities));
      }
    }
  }, [awbDataList]);

  const handlePrint = () => {
    const w = window.open();
    if (w) {
      w.document.write(
        `<style>@page {size: 4in 4in;margin: 0.08in 0.08in 0.08in 0.16in;}p{margin:0}.page-break{page-break-after:always;}</style><body style="margin: 0">${
          document.getElementById("multi-awb-slip-outer")?.innerHTML || ""
        }</body>`
      );
      w.print();
      w.close();
    }
  };

  if (!isOpen) return null;

  const SingleLabel = ({ awbData, index, totalForThisAwb }: { awbData: any; index: number; totalForThisAwb: number }) => {
    const scale = 1.25;
    const s = (val: number) => `${val * scale}px`;

    const shipperCity = awbData?._assign_to_booking?.shipper_city || awbData?.shipper_city || "";
    const destCity = awbData?.destination || "";
    const viaCode = VIA_ROUTES[shipperCity.toUpperCase()]?.[destCity.toUpperCase()];
    const originCode = cityCodes[shipperCity] || "";
    const destCode = cityCodes[destCity] || "";

    return (
      <div
        style={{
          width: s(384),
          height: s(384),
          border: `${s(2)} solid #000`,
          boxSizing: "border-box",
          padding: s(8),
          fontFamily: "sans-serif",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          marginBottom: s(8),
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: s(4),
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>Book Date:</span>
            <span style={{ fontWeight: "bold" }}>
              {(() => {
                const dateStr = awbData?._assign_to_booking?.date || awbData?.bookingDate || "";
                if (!dateStr) return "-";
                return dateStr.includes("T")
                  ? moment(dateStr).format("DD-MM-YYYY")
                  : moment(dateStr, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY");
              })()}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: s(10) }}>
            {viaCode ? (
              <>
                <span style={{ fontSize: s(24), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>{originCode}</span>
                <span style={{ fontSize: s(18), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>⟶</span>
                <span style={{ fontSize: s(36), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>{viaCode}</span>
                <span style={{ fontSize: s(18), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>⟶</span>
                <span style={{ fontSize: s(24), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>{destCode}</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: s(36), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>{originCode}</span>
                <span style={{ fontSize: s(22), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>⟶</span>
                <span style={{ fontSize: s(36), fontWeight: "bold", lineHeight: 1, fontFamily: "monospace" }}>{destCode}</span>
              </>
            )}
          </div>
        </div>

        {/* Sub Labels */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: s(12),
            marginBottom: s(10),
            padding: `${s(5)} 0`,
            borderTop: "1px solid black",
            borderBottom: "1px solid black",
            lineHeight: 1,
          }}
        >
          <div>
            <p style={{ marginBottom: s(5) }}>
              <strong>{awbData?.consignee}</strong>
            </p>
            <span style={{ fontSize: s(16) }}>
              {awbData?.address} | <b style={{ fontSize: s(18) }}>{awbData?.pincode}</b>
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: s(14),
            marginBottom: s(10),
          }}
        >
          {/* Weight and Mbag */}
          <div style={{ fontSize: s(18), marginRight: s(5) }}>
            {!!awbData?.isFod && (
              <div style={{ fontSize: s(24), fontWeight: 600, lineHeight: 1.5 }}>
                <p>
                  FOD: <b>✓</b>
                </p>
              </div>
            )}
            {!!awbData?.isCod && (
              <div style={{ fontSize: s(24), fontWeight: 600, lineHeight: 1.5 }}>
                <p>
                  COD: <b>✓</b>
                </p>
              </div>
            )}
            <div style={{ fontSize: s(14), lineHeight: 1.5 }}>
              <p>
                10:30: <b>{awbData?.is10_30 ? "Y" : "N"}</b>
              </p>
              <p>
                Holiday: <b>{awbData?.holiday ? "Y" : "N"}</b>
              </p>
              <p>
                Type: <b>{awbData?.dox_or_non_dox ? "Dox" : "Non Dox"}</b>
              </p>
            </div>
          </div>

          {/* QR Code Box */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: s(4),
            }}
          >
            <b style={{ fontSize: s(18), lineHeight: 1.5 }}>
              <u>Ref #: {awbData?._assign_to_booking?.pickup_code || awbData?.pickup_code || "N/A"}</u>
            </b>
            <QRCode value={awbData?.awb_no || ""} size={scale * 100} level="M" />
            <span style={{ fontSize: s(24), lineHeight: 1, marginTop: s(5) }}>
              <b>{awbData?.awb_no}</b>
            </span>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <div style={{ fontWeight: "bold" }}>
            <Img src={"/logo.jpg"} style={{ height: s(50) }} />
          </div>
          <div>
            <div style={{ fontSize: s(30), lineHeight: 1, textAlign: "right", marginTop: s(2) }}>
              MPS:{" "}
              <strong>
                {index + 1}/{totalForThisAwb}
              </strong>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Generate all labels: for each AWB, generate innerItemsNos labels
  const allLabels: { awbData: any; index: number; totalForThisAwb: number }[] = [];
  awbDataList.forEach((awbData) => {
    const count = awbData?.innerItemsNos || 1;
    for (let i = 0; i < count; i++) {
      allLabels.push({ awbData, index: i, totalForThisAwb: count });
    }
  });

  return (
    <div className="fixed inset-0 bg-black-900 bg-opacity-50 flex items-center justify-center font-inter" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-2xl shadow-lg p-6 relative max-w-[95vw]">
        {/* Close Button */}
        <button
          className="absolute top-6 right-6 text-gray-600 hover:text-black bg-gray-300 px-2 text-lg font-bold rounded-md"
          onClick={onClose}
        >
          &times;
        </button>

        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          Airway Bill Slip ({awbDataList.length} AWB{awbDataList.length > 1 ? "s" : ""}, {allLabels.length} Label
          {allLabels.length > 1 ? "s" : ""})
        </h2>

        {loadingCities && <p className="text-gray-500 mb-2">Loading city codes...</p>}

        <div className="my-4 overflow-auto max-h-[70vh]" id="multi-awb-slip-outer">
          {allLabels.map((label, idx) => (
            <div key={`label-${label.awbData.awb_no}-${label.index}`} className={idx < allLabels.length - 1 ? "page-break" : ""}>
              <SingleLabel awbData={label.awbData} index={label.index} totalForThisAwb={label.totalForThisAwb} />
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex gap-4">
          <button onClick={handlePrint} type="button" className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700">
            Print All ({allLabels.length})
          </button>
        </div>
      </div>
    </div>
  );
};
